'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { getProducts } from '@/actions';
import { TProductData } from '@/types/product';

type Product = TProductData;

interface ProductInputProps {
  value: string;
  onChange: (value: string) => void;
  onProductSelect: (product: Product) => void;
  placeholder?: string;
  disabled?: boolean;
  currentPrice?: number; // ← number is fine
}

export const ProductInput: React.FC<ProductInputProps> = ({
  value,
  onChange,
  onProductSelect,
  placeholder = 'Type to search products...',
  disabled = false,
  currentPrice,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load products once
  useEffect(() => {
    (async () => {
      try {
        const { products } = await getProducts();
        setProducts(products);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    })();
  }, []);

  // Derived flags
  const isExistingProduct = useMemo(() => {
    if (!value || currentPrice == null) return false;
    const name = value.toLowerCase();
    const match = products.find(
      (p) =>
        p.name.toLowerCase() === name &&
        String(p.price) === String(currentPrice)
    );
    return !!match;
  }, [products, value, currentPrice]);

  // 🔑 Derive filtered list instead of setting state in an effect
  const filteredProducts = useMemo(() => {
    if (!isUserTyping) return [];
    const v = value.trim().toLowerCase();
    if (!v) return [];
    return products.filter(
      (p) => p.name.toLowerCase().includes(v) && p.name.toLowerCase() !== v
    );
  }, [products, value, isUserTyping]);

  const showDropdown = filteredProducts.length > 0;

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setIsUserTyping(true);
    onChange(newValue);

    // When input is cleared, reset index so Enter won’t select stale item
    if (!newValue.trim()) setSelectedIndex(-1);
  };

  const handleProductClick = (product: Product) => {
    onChange(product.name);
    onProductSelect(product);
    setSelectedIndex(-1);
    setIsUserTyping(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredProducts.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredProducts.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleProductClick(filteredProducts[selectedIndex]);
        }
        break;
      case 'Escape':
        setSelectedIndex(-1);
        setIsUserTyping(false);
        break;
    }
  };

  const handleBlur = () => {
    // Delay hiding dropdown to allow clicks inside it
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setSelectedIndex(-1);
        setIsUserTyping(false);
      }
    }, 150);
  };

  const handleFocus = () => {
    // Don’t show suggestions for an exact match (editing existing item)
    if (isExistingProduct) setIsUserTyping(false);
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className={
          isExistingProduct ? 'border-green-200 bg-white text-black' : ''
        }
      />

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border text-black border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredProducts.slice(0, 5).map((product, index) => (
            <div
              key={product.id}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex justify-between items-center ${
                index === selectedIndex ? 'bg-gray-100' : ''
              }`}
              onClick={() => handleProductClick(product)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="text-sm flex flex-col">
                <span>{product.name}</span>
                {product.sku && (
                  <span className="text-xs text-gray-500">{product.sku}</span>
                )}
              </span>
              <span className="text-sm text-gray-500">{product.price}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
